import {useTranslation} from '@aboutyou/i18n';
import {ModalDialog, ProgressButton} from '@aboutyou/ui-components';
import {useUser} from '@aboutyou/user';
import React, {FC, useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';
import {useIsAuthenticated} from '../../../../features/Authentication/hooks/useIsAuthenticated';
import {AppInstallTeaser as AppInstallTeaserFixed} from '../../../Campaigns/AppInstallTeaser/AppInstallTeaser';
import {AppInstallTeaserDesktop} from '../../../Campaigns/AppInstallTeaserDesktop/AppInstallTeaserDesktop';

interface OverlayProps {
  onTimeElapsed: () => void;
  onCancel: () => void;
  onButtonClick: () => void;
  progressDuration: number;
  withDesktopTeaser: boolean;
}

export const CampaignOverlay: FC<OverlayProps> = ({
  onTimeElapsed,
  onCancel,
  onButtonClick,
  progressDuration,
  withDesktopTeaser,
}) => {
  const {t} = useTranslation();
  const timer = useRef<number>(-1);
  const {isAuthenticated} = useIsAuthenticated();
  const {data} = useUser();
  const [isAnimationCancelled, setIsAnimationCancelled] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      onTimeElapsed();
      onCancel();
    }, progressDuration);
    timer.current = timeout;
    return () => {
      clearTimeout(timeout);
    };
  }, [onTimeElapsed, onCancel]);

  const onCancelHandler = () => {
    clearTimeout(timer.current);
    onCancel();
  };

  const inputTouchHandler = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      setIsAnimationCancelled(true);
    }
  };

  const getTeaserTitle = () => {
    if (data) {
      return isAuthenticated
        ? t('Your basket will be synchronized automatically.')
        : t(
            "Your basket will be synchronized automatically once you've logged in.",
          );
    }
    return t(
      "Your basket will be synchronized automatically once you've logged in.",
    );
  };

  const CampaignImplementation: React.FC = () =>
    withDesktopTeaser ? (
      <AppInstallTeaserDesktop
        onCancel={onCancelHandler}
        onInputTouched={inputTouchHandler}
        title={t('Up to -50% off EVERYTHING in the APP')}
        subTitle={getTeaserTitle()}
      />
    ) : (
      <AppInstallTeaser
        setIsCampaignVisible={visibility => !visibility && onCancelHandler()}
        buttonOptions={{
          closeable: true,
          showInformationButton: true,
          showStoreButtons: false,
        }}
        title={getTeaserTitle()}
        headline={t('Up to -50% off EVERYTHING in the APP')}
      />
    );

  return (
    <CustomModalDialog
      dataTestId="preCheckoutAppInstallTeaser"
      open
      onClose={onCancelHandler}
      contentAlignment="center"
      contentTransparent
      contentFitWidth
      showCloseIcon={false}
      bodyContent={
        <>
          <CampaignWrapper>
            <CampaignImplementation />
          </CampaignWrapper>
          <StyledProgressButton
            $withDesktopTeaser={withDesktopTeaser}
            data-testid="preCheckoutAppInstallProgressButton"
            onClick={onButtonClick}
            progressDuration={progressDuration}
            label={t('Proceed to checkout')}
            isProgressAnimationPaused={isAnimationCancelled}
          />
        </>
      }
    />
  );
};

const CustomModalDialog = styled(ModalDialog)`
  && > #modalContent {
    width: 747px;
  }
`;

const AppInstallTeaser = styled(AppInstallTeaserFixed)`
  position: relative;
  margin: 0;
  width: 100%;
  padding-top: 1rem;
`;

const CampaignWrapper = styled.div`
  margin-bottom: 1rem;
`;

const StyledProgressButton = styled(ProgressButton)<{
  $withDesktopTeaser: boolean;
}>`
  ${({$withDesktopTeaser}) =>
    $withDesktopTeaser &&
    css`
      width: 33%;
      margin: 0 1px 1px auto;
    `}
`;
